import type { Scene, SceneGroup } from '@lumen5/luminary';

import { getInterleavedTranscript, interleavedTokenToString } from './transcript-tokens';

import type { RootState } from 'js/src/libs/RootState';
import {
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenBaseAssetSourceDuration,
    isAIAvatarSceneGroup,
    isSceneAnOverlay,
    isVoiceoverSceneGroup,
    translateVideoTimeToTranscriptTime,
} from 'js/src/luminary/luminary-selectors';
import {
    getVoicedrivenSceneEndTime,
    getVoicedrivenSceneStartTime,
} from 'js/src/luminary/voicedriven-scenes';

/**
 * One scene's slice of its scene group's transcript: the transcript-time span the scene plays
 * over, the words inside that span, and the scene's role.
 *
 * - `overlay`: an overlay scene layered on top of the waveform/talking-head base.
 * - `uncovered`: a base scene in a voiceover group — a coverage gap where the waveform is visible.
 * - `speaker`: a base scene in a talking-head / AI-avatar group — the speaker is on screen, which
 *   is the normal, intended state (not a gap).
 */
export type ScenePartition = {
    scene: Scene;
    startMillis: number; // transcript-time
    endMillis: number; // transcript-time
    words: string[];
    role: 'overlay' | 'uncovered' | 'speaker';
};

/**
 * Total, non-overlapping, timeline-ordered partition of a voice-driven scene group's interleaved
 * (word + pause) transcript across its scenes. In a voice-driven group scenes tile the timeline, so
 * every token is owned by exactly one scene; assigning each token to its scene partitions the
 * transcript, which is what makes concatenating the scenes' slices reproduce the full transcript
 * with each token appearing exactly once.
 *
 * This is the single source of truth for "which transcript tokens does each scene own" — the
 * scene-coverage serialization, the coverage-gap report, and the overlay-collision check all
 * derive from it, so the token↔scene time math lives in exactly one place.
 *
 * Returns null when there is no transcript to partition (non-voicedriven / text-on-media groups).
 */
export function partitionTranscriptByScene(
    state: RootState,
    sceneGroup: SceneGroup,
): ScenePartition[] | null {
    // Partition over the canonical interleaved (word + pause) token list — the exact tokens the
    // agent reads — so each scene's word slice and the overlay-collision ranges include pauses.
    const transcriptTokens = getInterleavedTranscript(state, sceneGroup);
    if (transcriptTokens.length === 0) {
        return null;
    }

    // A base (non-overlay) scene is an uncovered gap only in a voiceover group where the waveform
    // is otherwise visible. In talking-head / AI-avatar groups the base scene shows the speaker,
    // which is the intended state rather than a gap.
    const baseRole: 'uncovered' | 'speaker' =
        isVoiceoverSceneGroup(sceneGroup) && !isAIAvatarSceneGroup(sceneGroup)
            ? 'uncovered'
            : 'speaker';

    const playbackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
    const baseAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);

    const partitions: ScenePartition[] = sceneGroup.scenes.map((scene) => {
        const startMillis = translateVideoTimeToTranscriptTime(
            getVoicedrivenSceneStartTime(scene),
            playbackRegions,
            baseAssetSourceDuration,
            sceneGroup.startTime,
        );
        const endMillis = translateVideoTimeToTranscriptTime(
            getVoicedrivenSceneEndTime(scene),
            playbackRegions,
            baseAssetSourceDuration,
            sceneGroup.startTime,
            { includeTrailingCut: false },
        );
        const role = isSceneAnOverlay(state, scene) ? 'overlay' : baseRole;
        return { scene, startMillis, endMillis, words: [], role };
    });

    // Assign each token to exactly one scene by its start time, using half-open scene intervals
    // [startMillis, endMillis). This is a true partition: unlike an inclusive time-overlap test, a
    // token whose span straddles a scene boundary (a pause is a real span, unlike a word's tiny
    // one) lands in exactly the scene where it begins rather than in both neighbours. A leading
    // token (startTime before the first boundary) falls to the first scene; a trailing token (past
    // the last boundary) is absorbed by the final scene so no token is ever dropped. Words are
    // assigned identically to the previous overlap filter — a word's start lies within its scene —
    // so existing partition behaviour is preserved and only the straddle case changes.
    if (partitions.length > 0) {
        const finalPartition = partitions.reduce((latest, partition) =>
            partition.endMillis > latest.endMillis ? partition : latest,
        );
        for (const token of transcriptTokens) {
            const owner =
                partitions.find(
                    (partition) =>
                        partition.startMillis <= token.startTime &&
                        token.startTime < partition.endMillis,
                ) ?? finalPartition;
            owner.words.push(interleavedTokenToString(token));
        }
    }

    return partitions;
}
